import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Loader, Message } from 'rsuite';
import Swal from 'sweetalert2';
import { Scanner } from '@yudiel/react-qr-scanner';

import {
	bootstrapSponsorScan,
	captureLead,
	clearLastCaptured,
	reset,
	selectRepresentative,
} from '~/store/sponsorScan';
import { useAppDispatch, useAppSelector } from '~/hooks/hooks';
import type { EventState } from '~/store';

import styles from './SponsorLeadCapture.module.less';

/**
 * Booth-side AI matchmaking flow. URL: /event/:eventAccessCode/sponsor-scan/:sponsorId?t=<token>
 * Stages: bootstrap → representative picker (sticky) → camera (re-enables itself between scans).
 */
export const SponsorLeadCapture = () => {
	const dispatch = useAppDispatch();
	const { sponsorId } = useParams<{ eventAccessCode: string; sponsorId: string }>();
	const [searchParams] = useSearchParams();
	const exchangeToken = searchParams.get('t');

	const { status, sponsor, selectedRepresentativeId, lastCaptured, error, capturing } =
		useAppSelector((state: EventState) => state.sponsorScan);

	// Local: short-lived guard so a single QR isn't fired multiple times per second.
	const [scannerArmed, setScannerArmed] = useState(true);
	const recentBarcodeRef = useRef<{ value: string; at: number } | null>(null);

	useEffect(() => {
		if (!sponsorId) return;
		dispatch(bootstrapSponsorScan(sponsorId, exchangeToken));
		return () => {
			dispatch(reset());
		};
	}, [sponsorId, exchangeToken, dispatch]);

	useEffect(() => {
		if (!lastCaptured) return;
		const { lead, deduped } = lastCaptured;
		Swal.fire({
			title: deduped ? 'Updated' : 'Lead captured',
			text: deduped
				? `${lead.participantName} already in your leads — touchpoint added.`
				: `${lead.participantName} added to your leads.`,
			icon: 'success',
			timer: 1600,
			showConfirmButton: false,
		}).then(() => {
			dispatch(clearLastCaptured());
			setScannerArmed(true);
		});
		// TODO(post-scan-rating): subscribe here for the hot/warm/cold rating bottom sheet
		// once that UI ships. Hook intentionally left in place; payload is `lead`.
	}, [lastCaptured, dispatch]);

	useEffect(() => {
		if (!error) return;
		Swal.fire({ title: 'Ups!', text: error, icon: 'error', confirmButtonText: 'Ok' });
		setScannerArmed(true);
	}, [error]);

	const selectedRep = useMemo(
		() => sponsor?.representatives.find(r => r.id === selectedRepresentativeId) ?? null,
		[sponsor, selectedRepresentativeId],
	);

	const handlePickRepresentative = (repId: string) => {
		if (!sponsorId) return;
		dispatch(selectRepresentative(sponsorId, repId));
	};

	const handleSwitchRepresentative = () => {
		if (!sponsorId) return;
		dispatch(selectRepresentative(sponsorId, null));
	};

	const handleScan = (barcode: string) => {
		if (!sponsorId || !selectedRepresentativeId || !scannerArmed || capturing) return;

		const trimmed = (barcode || '').trim();
		if (!trimmed) return;

		// Defensive dedup: if the same barcode fires twice within ~2s we treat the
		// second hit as a stray (camera tap, accidental re-read). The backend dedups
		// anyway, but suppressing the request avoids a noisy toast loop.
		const now = Date.now();
		const recent = recentBarcodeRef.current;
		if (recent && recent.value === trimmed && now - recent.at < 2000) return;
		recentBarcodeRef.current = { value: trimmed, at: now };

		setScannerArmed(false);
		dispatch(captureLead(sponsorId, trimmed, selectedRepresentativeId));
	};

	if (!sponsorId) {
		return <Message type="error">Missing sponsor id in URL.</Message>;
	}

	if (status === 'loading' || status === 'idle') {
		return <Loader backdrop center size="lg" content="Loading booth scanner…" />;
	}

	if (status === 'error' || !sponsor) {
		return (
			<div className={styles.container}>
				<Message type="error">{error ?? 'Could not load booth scanner.'}</Message>
				<p className={styles.subtle}>
					Open the sponsor portal and tap <strong>Scan</strong> to generate a fresh link.
				</p>
			</div>
		);
	}

	if (!selectedRep) {
		return (
			<div className={styles.container}>
				<header className={styles.header}>
					{sponsor.logoUrl && (
						<img src={sponsor.logoUrl} alt={sponsor.sponsorName} className={styles.logo} />
					)}
					<h2 className={styles.title}>{sponsor.sponsorName}</h2>
					<p className={styles.subtle}>{sponsor.eventName}</p>
				</header>

				<h3 className={styles.sectionTitle}>Who is scanning?</h3>
				{sponsor.representatives.length === 0 ? (
					<Message type="warning">
						No booth representatives configured yet. Open the sponsor portal{' '}
						<strong>Team</strong> page and add at least one representative.
					</Message>
				) : (
					<div className={styles.repGrid}>
						{sponsor.representatives.map(rep => (
							<button
								key={rep.id}
								type="button"
								className={styles.repTile}
								onClick={() => handlePickRepresentative(rep.id)}
							>
								<span className={styles.repName}>{rep.name}</span>
								{rep.role && <span className={styles.repRole}>{rep.role}</span>}
							</button>
						))}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<header className={styles.headerSlim}>
				<div>
					<span className={styles.sponsorChip}>{sponsor.sponsorName}</span>
					<span className={styles.repChip}>Scanning as {selectedRep.name}</span>
				</div>
				<Button appearance="link" size="sm" onClick={handleSwitchRepresentative}>
					Switch rep
				</Button>
			</header>

			<div className={styles.qrContent}>
				<div className={styles.qrFrame}>
					<Scanner
						scanDelay={400}
						onError={() => {
							/* Camera-permission errors fall to the rsuite Message below via state. */
						}}
						onScan={data => {
							if (data.length > 0) handleScan(data[0].rawValue);
						}}
						styles={{ container: { width: '100%' } }}
					/>
				</div>
				{capturing && <Loader center size="sm" content="Saving…" />}
				<p className={styles.subtle}>
					Point at a participant badge to capture a lead.
					{sponsor.boothLabel && <> · Booth {sponsor.boothLabel}</>}
				</p>
			</div>
		</div>
	);
};
